import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { Papel } from '@erp/shared';
import { EstadoVazio } from '@erp/ui';
import { Layout } from './componentes/Layout';
import { useSessao } from './lib/sessao';
import { Entrar } from './paginas/Entrar';
import { EntrarSuporte } from './paginas/EntrarSuporte';
import { Privacidade, TermosDeUso } from './paginas/legal/Termos';

/** Cada tela vira um pedaço separado, baixado só quando abre (o painel inicial fica leve). */
function pagina<K extends string>(carregar: () => Promise<Record<K, ComponentType>>, nome: K) {
  return lazy(() => carregar().then((m) => ({ default: m[nome] })));
}

const Financeiro = pagina(() => import('./paginas/financeiro/Financeiro'), 'Financeiro');
const ImportarContas = pagina(
  () => import('./paginas/financeiro/ImportarContas'),
  'ImportarContas',
);
const Financiamento = pagina(
  () => import('./paginas/financiamento/Financiamento'),
  'Financiamento',
);
const Fiscal = pagina(() => import('./paginas/fiscal/Fiscal'), 'Fiscal');
const Relatorios = pagina(() => import('./paginas/Relatorios'), 'Relatorios');
const Inteligencia = pagina(() => import('./paginas/inteligencia/Inteligencia'), 'Inteligencia');
const Rede = pagina(() => import('./paginas/rede/Rede'), 'Rede');
const Planos = pagina(() => import('./paginas/Planos'), 'Planos');
const Conheca = pagina(() => import('./paginas/Conheca'), 'Conheca');
const Cadastro = pagina(() => import('./paginas/Cadastro'), 'Cadastro');
const ConfirmarCadastro = pagina(() => import('./paginas/Cadastro'), 'ConfirmarCadastro');
const Ajuda = pagina(() => import('./paginas/Ajuda'), 'Ajuda');
const ArtigoAjuda = pagina(() => import('./paginas/Ajuda'), 'ArtigoAjuda');
const Ajustes = pagina(() => import('./paginas/Ajustes'), 'Ajustes');
const EsqueciSenha = pagina(() => import('./paginas/Senha'), 'EsqueciSenha');
const RedefinirSenha = pagina(() => import('./paginas/Senha'), 'RedefinirSenha');
const Convite = pagina(() => import('./paginas/Convite'), 'Convite');
const Agenda = pagina(() => import('./paginas/crm/Agenda'), 'Agenda');
const Atendimento = pagina(() => import('./paginas/crm/Atendimento'), 'Atendimento');
const ChatEquipe = pagina(() => import('./paginas/crm/ChatEquipe'), 'ChatEquipe');
const Cliente = pagina(() => import('./paginas/crm/Cliente'), 'Cliente');
const Clientes = pagina(() => import('./paginas/crm/Clientes'), 'Clientes');
const ImportarClientes = pagina(() => import('./paginas/crm/Clientes'), 'ImportarClientes');
const Equipe = pagina(() => import('./paginas/equipe/Equipe'), 'Equipe');
const Inicio = pagina(() => import('./paginas/Inicio'), 'Inicio');
const Leads = pagina(() => import('./paginas/Leads'), 'Leads');
const PainelDono = pagina(() => import('./paginas/PainelDono'), 'PainelDono');
const Site = pagina(() => import('./paginas/site/Site'), 'Site');
const AvaliacaoEntrada = pagina(() => import('./paginas/estoque/Avaliacoes'), 'AvaliacaoEntrada');
const Avaliacoes = pagina(() => import('./paginas/estoque/Avaliacoes'), 'Avaliacoes');
const DetalheVeiculo = pagina(() => import('./paginas/estoque/DetalheVeiculo'), 'DetalheVeiculo');
const Estoque = pagina(() => import('./paginas/estoque/Estoque'), 'Estoque');
const Importar = pagina(() => import('./paginas/estoque/Importar'), 'Importar');
const NovoVeiculo = pagina(() => import('./paginas/estoque/NovoVeiculo'), 'NovoVeiculo');
const Vistorias = pagina(() => import('./paginas/estoque/Vistorias'), 'Vistorias');
const VistoriaExecucao = pagina(() => import('./paginas/estoque/Vistorias'), 'VistoriaExecucao');
const OrdensServico = pagina(() => import('./paginas/estoque/OrdensServico'), 'OrdensServico');
const OrdemServicoDetalhe = pagina(
  () => import('./paginas/estoque/OrdensServico'),
  'OrdemServicoDetalhe',
);
const Eventos = pagina(() => import('./paginas/eventos/Eventos'), 'Eventos');
const DetalheEvento = pagina(() => import('./paginas/eventos/DetalheEvento'), 'DetalheEvento');
const AvaliarCarro = pagina(() => import('./paginas/AvaliarCarro'), 'AvaliarCarro');
const AssinarContrato = pagina(() => import('./paginas/AssinarContrato'), 'AssinarContrato');
const VerificarContrato = pagina(() => import('./paginas/AssinarContrato'), 'VerificarContrato');

function Protegida({ papeis, children }: { papeis?: Papel[]; children: React.ReactNode }) {
  const { usuario } = useSessao();
  if (!usuario) return <Navigate to="/entrar" replace />;
  if (papeis && !papeis.includes(usuario.papel)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Home() {
  const { usuario } = useSessao();
  return usuario?.papel === 'vendedor' ? <Inicio /> : <PainelDono />;
}

function Carregando() {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
      <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Carregando" />
    </div>
  );
}

export function App() {
  const { carregando } = useSessao();
  if (carregando) return <Carregando />;
  return (
    <Suspense fallback={<Carregando />}>
      <Routes>
        <Route path="/entrar" element={<Entrar />} />
        <Route path="/suporte/entrar" element={<EntrarSuporte />} />
        <Route path="/termos" element={<TermosDeUso />} />
        <Route path="/privacidade" element={<Privacidade />} />
        <Route path="/planos" element={<Planos />} />
        <Route path="/conheca" element={<Conheca />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/cadastro/confirmar/:token" element={<ConfirmarCadastro />} />
        <Route path="/convite/:token" element={<Convite />} />
        <Route path="/avaliar/:token" element={<AvaliarCarro />} />
        <Route path="/assinar/:token" element={<AssinarContrato />} />
        <Route path="/verificar/:codigo" element={<VerificarContrato />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/redefinir-senha/:token" element={<RedefinirSenha />} />
        <Route
          element={
            <Protegida>
              <Layout />
            </Protegida>
          }
        >
          <Route index element={<Home />} />
          <Route path="estoque" element={<Estoque />} />
          <Route
            path="estoque/novo"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <NovoVeiculo />
              </Protegida>
            }
          />
          <Route
            path="estoque/importar"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <Importar />
              </Protegida>
            }
          />
          <Route
            path="estoque/avaliacoes"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <Avaliacoes />
              </Protegida>
            }
          />
          <Route
            path="estoque/avaliacoes/nova"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <AvaliacaoEntrada />
              </Protegida>
            }
          />
          <Route
            path="estoque/avaliacoes/:id"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <AvaliacaoEntrada key="editar" />
              </Protegida>
            }
          />
          <Route path="estoque/:id" element={<DetalheVeiculo />} />
          <Route
            path="eventos"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Eventos />
              </Protegida>
            }
          />
          <Route
            path="eventos/:id"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <DetalheEvento />
              </Protegida>
            }
          />
          <Route
            path="oficina"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <OrdensServico />
              </Protegida>
            }
          />
          <Route
            path="oficina/:id"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <OrdemServicoDetalhe />
              </Protegida>
            }
          />
          <Route
            path="vistorias"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Vistorias />
              </Protegida>
            }
          />
          <Route
            path="vistorias/:id"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <VistoriaExecucao />
              </Protegida>
            }
          />
          <Route
            path="leads"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Leads />
              </Protegida>
            }
          />
          <Route
            path="relatorios"
            element={
              <Protegida papeis={['dono', 'gerente', 'financeiro']}>
                <Relatorios />
              </Protegida>
            }
          />
          <Route
            path="fiscal"
            element={
              <Protegida papeis={['dono', 'gerente', 'financeiro']}>
                <Fiscal />
              </Protegida>
            }
          />
          <Route
            path="clientes"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Clientes />
              </Protegida>
            }
          />
          <Route
            path="clientes/importar"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <ImportarClientes />
              </Protegida>
            }
          />
          <Route
            path="clientes/:id"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Cliente />
              </Protegida>
            }
          />
          <Route
            path="atendimento"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Atendimento />
              </Protegida>
            }
          />
          <Route
            path="atendimento/:id"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Atendimento />
              </Protegida>
            }
          />
          <Route
            path="agenda"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <Agenda />
              </Protegida>
            }
          />
          <Route
            path="chat"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor']}>
                <ChatEquipe />
              </Protegida>
            }
          />
          <Route
            path="equipe"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor', 'financeiro']}>
                <Equipe />
              </Protegida>
            }
          />
          <Route
            path="financeiro"
            element={
              <Protegida papeis={['dono', 'financeiro']}>
                <Financeiro />
              </Protegida>
            }
          />
          <Route
            path="financeiro/importar"
            element={
              <Protegida papeis={['dono', 'financeiro']}>
                <ImportarContas />
              </Protegida>
            }
          />
          <Route
            path="financiamento"
            element={
              <Protegida papeis={['dono', 'gerente', 'vendedor', 'financeiro']}>
                <Financiamento />
              </Protegida>
            }
          />
          <Route
            path="site"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <Site />
              </Protegida>
            }
          />
          <Route
            path="inteligencia"
            element={
              <Protegida papeis={['dono', 'gerente', 'financeiro', 'vendedor']}>
                <Inteligencia />
              </Protegida>
            }
          />
          <Route
            path="rede"
            element={
              <Protegida papeis={['dono', 'gerente']}>
                <Rede />
              </Protegida>
            }
          />
          <Route path="ajuda" element={<Ajuda />} />
          <Route path="ajuda/:slug" element={<ArtigoAjuda />} />
          <Route
            path="ajustes"
            element={
              <Protegida>
                <Ajustes />
              </Protegida>
            }
          />
          <Route
            path="ajustes/:secao"
            element={
              <Protegida>
                <Ajustes />
              </Protegida>
            }
          />
          <Route path="*" element={<EstadoVazio titulo="Página não encontrada" />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
